import React from 'react';
import { RichTextTooltip } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/tooltip/tooltip';
import { RichTextTooltipContent } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/tooltip/tooltip';
import { RichTextTooltipTrigger } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/tooltip/tooltip';
import 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button-colors.scss';
import 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button-group.scss';
import 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button.scss';

type PlatformShortcuts = Record<string, string>;

export interface RichTextButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  showTooltip?: boolean;
  tooltip?: React.ReactNode;
  shortcutKeys?: string;
}

export const RICH_TEXT_MAC_SYMBOLS: PlatformShortcuts = {
  ctrl: '⌘',
  alt: '⌥',
  shift: '⇧',
} as const;

export const richTextFormatShortcutKey = (key: string, isMac: boolean) => {
  if (isMac) {
    const lowerKey = key.toLowerCase();
    return RICH_TEXT_MAC_SYMBOLS[lowerKey] || key.toUpperCase();
  }
  return key.charAt(0).toUpperCase() + key.slice(1);
};

export const richTextParseShortcutKeys = (
  shortcutKeys: string | undefined,
  isMac: boolean,
) => {
  if (!shortcutKeys) return [];

  return shortcutKeys
    .split('-')
    .map((key) => key.trim())
    .map((key) => richTextFormatShortcutKey(key, isMac));
};

export const RichTextShortcutDisplay: React.FC<{ shortcuts: string[] }> = ({
  shortcuts,
}) => {
  if (shortcuts.length === 0) return null;

  return (
    <div>
      {shortcuts.map((key, index) => (
        <React.Fragment key={index}>
          {index > 0 && <kbd>+</kbd>}
          <kbd>{key}</kbd>
        </React.Fragment>
      ))}
    </div>
  );
};

export const RichTextButton = React.forwardRef<
  HTMLButtonElement,
  RichTextButtonProps
>(
  (
    {
      className = '',
      children,
      tooltip,
      showTooltip = true,
      shortcutKeys,
      'aria-label': ariaLabel,
      ...props
    },
    ref,
  ) => {
    const isMac = React.useMemo(
      () =>
        typeof navigator !== 'undefined' &&
        navigator.platform.toLowerCase().includes('mac'),
      [],
    );

    const shortcuts = React.useMemo(
      () => richTextParseShortcutKeys(shortcutKeys, isMac),
      [shortcutKeys, isMac],
    );

    if (!tooltip || !showTooltip) {
      return (
        <button
          className={`tiptap-button ${className}`.trim()}
          ref={ref}
          aria-label={ariaLabel}
          {...props}
        >
          {children}
        </button>
      );
    }

    return (
      <RichTextTooltip delay={200}>
        <RichTextTooltipTrigger
          className={`tiptap-button ${className}`.trim()}
          ref={ref}
          aria-label={ariaLabel}
          {...props}
        >
          {children}
        </RichTextTooltipTrigger>
        <RichTextTooltipContent>
          {tooltip}
          <RichTextShortcutDisplay shortcuts={shortcuts} />
        </RichTextTooltipContent>
      </RichTextTooltip>
    );
  },
);

RichTextButton.displayName = 'Button';

export default RichTextButton;
